import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save, Plus, X } from "lucide-react";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/adminAuth";
import { useSiteContent } from "../../lib/siteContent";

const EMPTY_BILINGUAL = { tr: "", en: "" };

function ensureService(s) {
  return {
    slug: s?.slug || "",
    image: s?.image || "",
    eyebrow: { ...EMPTY_BILINGUAL, ...(s?.eyebrow || {}) },
    title: { ...EMPTY_BILINGUAL, ...(s?.title || {}) },
    description: { ...EMPTY_BILINGUAL, ...(s?.description || {}) },
    long: { ...EMPTY_BILINGUAL, ...(s?.long || {}) },
    bullets: {
      tr: Array.isArray(s?.bullets?.tr) ? s.bullets.tr : [],
      en: Array.isArray(s?.bullets?.en) ? s.bullets.en : [],
    },
  };
}

export default function AdminServices() {
  const { authedClient } = useAdminAuth();
  const { refresh: refreshSite } = useSiteContent();
  const [services, setServices] = useState([]);
  const [contact, setContact] = useState(null); // keep so PUT is whole object
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient.get("/admin/site");
      setServices((data.services || []).map(ensureService));
      setContact(data.contact || null);
      if ((data.services || []).length > 0 && expanded === null) setExpanded(data.services[0].slug);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [authedClient, expanded]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (slug, path, value) => {
    setServices((prev) =>
      prev.map((s) => {
        if (s.slug !== slug) return s;
        const next = { ...s };
        if (path.length === 1) next[path[0]] = value;
        else if (path.length === 2) next[path[0]] = { ...next[path[0]], [path[1]]: value };
        return next;
      })
    );
  };

  const updateBullet = (slug, lang, i, value) => {
    setServices((prev) =>
      prev.map((s) => {
        if (s.slug !== slug) return s;
        const arr = [...(s.bullets[lang] || [])];
        arr[i] = value;
        return { ...s, bullets: { ...s.bullets, [lang]: arr } };
      })
    );
  };
  const addBullet = (slug, lang) => {
    setServices((prev) =>
      prev.map((s) =>
        s.slug !== slug ? s : { ...s, bullets: { ...s.bullets, [lang]: [...(s.bullets[lang] || []), ""] } }
      )
    );
  };
  const removeBullet = (slug, lang, i) => {
    setServices((prev) =>
      prev.map((s) => {
        if (s.slug !== slug) return s;
        const arr = [...(s.bullets[lang] || [])];
        arr.splice(i, 1);
        return { ...s, bullets: { ...s.bullets, [lang]: arr } };
      })
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await authedClient.put("/admin/site", { services, contact });
      toast.success("Hizmetler kaydedildi");
      refreshSite();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-ink/55 text-sm py-12 text-center">Yükleniyor…</div>;

  return (
    <div data-testid="admin-services-page">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-7">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Hizmetler</h1>
          <p className="mt-1 text-sm text-ink/60">Sitede görünen 4 hizmetin Türkçe ve İngilizce içeriklerini buradan düzenleyin.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="services-save"
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" strokeWidth={1.5} />
          {saving ? "Kaydediliyor…" : "Tümünü Kaydet"}
        </button>
      </header>

      <div className="space-y-3">
        {services.map((s) => {
          const isOpen = expanded === s.slug;
          return (
            <section
              key={s.slug}
              data-testid={`service-editor-${s.slug}`}
              className="surface-card overflow-hidden"
            >
              <button
                onClick={() => setExpanded(isOpen ? null : s.slug)}
                className="w-full flex items-center justify-between px-6 py-5 hover:bg-cream-50"
              >
                <div className="text-left">
                  <span className="text-xs uppercase tracking-[0.22em] text-terracotta-400 font-medium">{s.slug}</span>
                  <p className="font-serif text-2xl text-ink mt-1">{s.title.tr || s.title.en || s.slug}</p>
                </div>
                <span className="text-ink/40">{isOpen ? "−" : "+"}</span>
              </button>

              {isOpen && (
                <div className="px-6 pb-7 border-t border-cream-300/60 pt-6 space-y-5">
                  <div>
                    <span className="admin-label">Görsel URL</span>
                    <input
                      data-testid={`service-${s.slug}-image`}
                      type="url"
                      value={s.image || ""}
                      onChange={(e) => update(s.slug, ["image"], e.target.value)}
                      placeholder="https://yourdomain.com/path/image.jpg"
                      className="admin-input"
                    />
                    {s.image && (
                      <div className="mt-3 w-32 aspect-[4/5] rounded-lg overflow-hidden border border-cream-300/60">
                        <img src={s.image} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>

                  <BilingualField label="Başlık üstü (eyebrow)" value={s.eyebrow} onChange={(lang, v) => update(s.slug, ["eyebrow", lang], v)} testid={`service-${s.slug}-eyebrow`} />
                  <BilingualField label="Başlık" value={s.title} onChange={(lang, v) => update(s.slug, ["title", lang], v)} testid={`service-${s.slug}-title`} />
                  <BilingualField label="Kısa açıklama" value={s.description} multiline onChange={(lang, v) => update(s.slug, ["description", lang], v)} testid={`service-${s.slug}-desc`} />
                  <BilingualField label="Uzun açıklama" value={s.long} multiline rows={5} onChange={(lang, v) => update(s.slug, ["long", lang], v)} testid={`service-${s.slug}-long`} />

                  <div className="grid md:grid-cols-2 gap-5">
                    {["tr", "en"].map((lang) => (
                      <div key={lang}>
                        <span className="admin-label">Maddeler — {lang.toUpperCase()}</span>
                        <div className="space-y-2">
                          {(s.bullets[lang] || []).map((b, i) => (
                            <div key={i} className="flex gap-2">
                              <input
                                data-testid={`service-${s.slug}-bullet-${lang}-${i}`}
                                value={b}
                                onChange={(e) => updateBullet(s.slug, lang, i, e.target.value)}
                                className="admin-input flex-1"
                              />
                              <button
                                type="button"
                                onClick={() => removeBullet(s.slug, lang, i)}
                                className="text-ink/40 hover:text-terracotta-500 px-2"
                              >
                                <X className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addBullet(s.slug, lang)}
                            data-testid={`service-${s.slug}-bullet-add-${lang}`}
                            className="inline-flex items-center gap-1 text-xs text-ink/60 hover:text-ink"
                          >
                            <Plus className="w-3 h-3" strokeWidth={1.5} />
                            Madde ekle
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function BilingualField({ label, value, onChange, multiline = false, rows = 3, testid = "" }) {
  return (
    <div>
      <span className="admin-label">{label}</span>
      <div className="grid md:grid-cols-2 gap-3">
        {["tr", "en"].map((lang) => (
          <div key={lang}>
            <span className="block text-[10px] uppercase tracking-widest text-ink/45 mb-1">{lang.toUpperCase()}</span>
            {multiline ? (
              <textarea
                data-testid={`${testid}-${lang}`}
                rows={rows}
                value={value?.[lang] || ""}
                onChange={(e) => onChange(lang, e.target.value)}
                className="admin-textarea"
              />
            ) : (
              <input
                data-testid={`${testid}-${lang}`}
                value={value?.[lang] || ""}
                onChange={(e) => onChange(lang, e.target.value)}
                className="admin-input"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
